import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { getInventoryMovements } from '../../api';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Search } from 'lucide-react';
import { toast } from 'sonner';

const MovementTypeBadge = ({ type }) => {
  const styles = {
    intake: 'bg-blue-100 text-blue-700',
    transfer: 'bg-indigo-100 text-indigo-700',
    deploy: 'bg-green-100 text-green-700',
    return: 'bg-amber-100 text-amber-700',
    replace: 'bg-purple-100 text-purple-700',
    scrap: 'bg-red-100 text-red-700',
    lost: 'bg-red-200 text-red-800',
    adjustment: 'bg-slate-100 text-slate-700',
  };
  return <span className={`px-2 py-0.5 text-xs rounded capitalize ${styles[type] || 'bg-slate-100'}`}>{type}</span>;
};

const MovementHistory = ({ user, setUser }) => {
  const [movements, setMovements] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    getInventoryMovements({ limit: 500 }).then(r => setMovements(r.data)).catch(() => toast.error('Failed to load'));
  }, []);

  const filtered = movements.filter(m => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [m.serial_number, m.item_type_name, m.from_holder_name, m.to_holder_name, m.triggered_by_user_name]
      .some(x => (x || '').toLowerCase().includes(s));
  });

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Movement History</h1>
          <p className="text-sm text-slate-500">Full audit log — every transfer, deployment, return, scrap</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="relative mb-4 max-w-md">
              <Search className="absolute left-2 top-3 h-4 w-4 text-slate-400" />
              <Input className="pl-8" placeholder="Search by item, serial, person..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-movements" />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 text-xs font-medium">When</th>
                    <th className="text-left p-2 text-xs font-medium">Type</th>
                    <th className="text-left p-2 text-xs font-medium">Item</th>
                    <th className="text-left p-2 text-xs font-medium">Qty</th>
                    <th className="text-left p-2 text-xs font-medium">From</th>
                    <th className="text-left p-2 text-xs font-medium">→ To</th>
                    <th className="text-left p-2 text-xs font-medium">By</th>
                    <th className="text-left p-2 text-xs font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(m => (
                    <tr key={m.id} className="border-b border-slate-100">
                      <td className="p-2 text-xs text-slate-600">{new Date(m.created_at).toLocaleString()}</td>
                      <td className="p-2"><MovementTypeBadge type={m.movement_type} /></td>
                      <td className="p-2">
                        <p className="text-sm">{m.item_type_name}</p>
                        {m.serial_number && <p className="text-xs font-mono text-indigo-700">{m.serial_number}</p>}
                      </td>
                      <td className="p-2 font-semibold">{m.quantity}</td>
                      <td className="p-2 text-xs">
                        <p>{m.from_holder_name || '-'}</p>
                        <p className="text-slate-500 capitalize">{(m.from_holder_type || '').replace('_', ' ')}</p>
                      </td>
                      <td className="p-2 text-xs">
                        <p className="font-medium text-green-700">{m.to_holder_name || '-'}</p>
                        <p className="text-slate-500 capitalize">{(m.to_holder_type || '').replace('_', ' ')}</p>
                      </td>
                      <td className="p-2 text-xs">{m.triggered_by_user_name}</td>
                      <td className="p-2 text-xs text-slate-600">{m.reason || '-'}</td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-slate-500">No movements found</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default MovementHistory;
